import { useEffect, useState } from "react";

const CURRENCY_CODE_PATTERN = /\(([A-Z]{3})\)/;

export const extractCurrencyCode = (
  currencyText?: string
): string | undefined => {
  if (!currencyText) return undefined;
  const match = currencyText.match(CURRENCY_CODE_PATTERN);
  return match ? match[1] : undefined;
};

type ConversionState =
  | { status: "idle" }
  | { status: "loading" }
  | { status: "success"; rate: number }
  | { status: "error" };

// Module-level cache: exchange rates barely move within a session, and this
// avoids refetching the same code across every country card that shares it.
const rateCache = new Map<string, number>();

export const useUsdConversionRate = (
  currencyCode?: string
): ConversionState => {
  const [state, setState] = useState<ConversionState>({ status: "idle" });

  useEffect(() => {
    if (!currencyCode) {
      setState({ status: "idle" });
      return;
    }

    if (currencyCode === "USD") {
      setState({ status: "success", rate: 1 });
      return;
    }

    const cached = rateCache.get(currencyCode);
    if (cached !== undefined) {
      setState({ status: "success", rate: cached });
      return;
    }

    let cancelled = false;
    setState({ status: "loading" });

    fetch(`https://open.er-api.com/v6/latest/${currencyCode}`)
      .then((res) => {
        if (!res.ok) throw new Error("Exchange rate request failed");
        return res.json();
      })
      .then((body) => {
        if (cancelled) return;
        const rate = body?.rates?.USD;
        if (typeof rate !== "number") throw new Error("Missing USD rate");
        rateCache.set(currencyCode, rate);
        setState({ status: "success", rate });
      })
      .catch(() => {
        if (!cancelled) setState({ status: "error" });
      });

    return () => {
      cancelled = true;
    };
  }, [currencyCode]);

  return state;
};
